import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, AttachFile, CheckCircle, Error } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../../components/MainContainer";
import MainHeader from "../../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../../components/MainHeaderButtonsWrapper";
import Title from "../../../components/Title";
import ConfirmationModal from "../../../components/ConfirmationModal";
import AgentIaSelector from "../../../components/AgentIaSelector";
import TableRowSkeleton from "../../../components/TableRowSkeleton";

import {
  listDocuments,
  uploadDocument,
  deleteDocument,
} from "../../../services/agentIa";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
}));

const STATUS_CHIP = {
  processing: { label: "Procesando...", color: "default" },
  ready: { label: "Listo", color: "primary" },
  failed: { label: "Error", color: "secondary" },
};

const AgentIaDocuments = () => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [agentId, setAgentId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!agentId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const data = await listDocuments(agentId);
      setDocuments(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      await uploadDocument(agentId, file);
      toast.success("Documento subido. Puede tardar unos segundos en procesarse.");
      fetchDocuments();
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDocument(agentId, deletingDoc.id);
      toast.success("Documento eliminado.");
      fetchDocuments();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingDoc(null);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingDoc && `¿Eliminar "${deletingDoc.original_name}"?`}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        El agente deja de usar este documento para responder. No se puede
        deshacer.
      </ConfirmationModal>
      <MainHeader>
        <Title>Documentación</Title>
        <MainHeaderButtonsWrapper>
          <AgentIaSelector value={agentId} onChange={setAgentId} />
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.docx,.md,.txt,.jpg,.jpeg,.png"
            onChange={handleFileSelected}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AttachFile />}
            disabled={!agentId || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Subiendo..." : "Subir archivo"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Typography variant="caption" color="textSecondary" style={{ padding: 8, display: "block" }}>
          PDF, DOCX, MD, TXT, JPG o PNG. Para imágenes (ej. esquemas de
          instalación), el agente genera una descripción por visión para
          poder encontrarlas, y puede adjuntar el archivo original cuando
          responda. Solo este agente va a consultar lo que subas acá — no
          se mezcla con la documentación de otros agentes.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Archivo</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Fragmentos</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => {
              const chipInfo = STATUS_CHIP[doc.status] || STATUS_CHIP.processing;
              return (
                <TableRow key={doc.id}>
                  <TableCell align="center">{doc.original_name}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={doc.error_message || ""}>
                      <Chip
                        size="small"
                        label={chipInfo.label}
                        color={chipInfo.color}
                        icon={
                          doc.status === "ready" ? (
                            <CheckCircle fontSize="small" />
                          ) : doc.status === "failed" ? (
                            <Error fontSize="small" />
                          ) : undefined
                        }
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">{doc.chunk_count}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingDoc(doc);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={4} />}
            {!loading && agentId && documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Este agente todavía no tiene documentos.
                </TableCell>
              </TableRow>
            )}
            {!agentId && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Crea un agente primero en la sección &ldquo;Agentes&rdquo;.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default AgentIaDocuments;
